import React, { useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { getAllUsersByProjectId } from '../../services/api/implementation/impl';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import { useParams } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const EditBlock = ({
  onClose,
  onSubmit,
  setIsEditingBlock,
  IsOpenAddNewBlock,
  handleEditBlock,
  onProceed,
  data,
  isSubmitting,
  buildingByProjectId,
  setIsOpenEditProject
}) => {
  const userId = Cookies.get('userId');
  const { id: projectId } = useParams();
  const operationsManagerId =
    data?.operations_manager_id ?? data?.operationsManager?.id ?? data?.operationsManager_id;

  const { register, handleSubmit, setValue, reset } = useForm({
    defaultValues: {
      name: data?.name || '',
      area: data?.area || '',
      building_id: data?.building_id?.toString() || '',
      operations_manager_id: operationsManagerId?.toString() || ''
    }
  });

  const { data: projectUsers } = useQuery({
    queryKey: ['projectUsers', projectId],
    queryFn: () => getAllUsersByProjectId(projectId, 1, 500),
    enabled: !!projectId
  });

  const operationsManagerOptions =
    projectUsers?.data
      ?.filter((user) => user.vertical === 'Operations' && user.level === 'Medium')
      .map((user) => ({
        value: user.id.toString(),
        label: user.name
      })) || [];

  // Create dropdown options for building permission numbers
  const buildingPermissionOptions = [
    ...(buildingByProjectId?.map((building) => ({
      value: building.id.toString(),
      label: building.permissionNo
    })) || [])
  ];

  const handleProceed = (formData) => {
    const payload = {
      name: formData.name,
      area: formData.area
    };

    if (formData.building_id) {
      const buildingId = parseInt(formData.building_id, 10);
      if (!Number.isNaN(buildingId)) {
        payload.building_id = buildingId;
      }
    }

    if (formData.operations_manager_id) {
      const operationsManagerId = parseInt(formData.operations_manager_id, 10);
      if (!Number.isNaN(operationsManagerId)) {
        payload.operations_manager_id = operationsManagerId;
      }
    }

    onProceed(payload);
  };

  // Prefill form when data arrives (handles async loading)
  useEffect(() => {
    if (data) {
      const managerId =
        data.operations_manager_id ?? data.operationsManager?.id ?? data.operationsManager_id;
      reset({
        name: data.name || '',
        area: data.area || '',
        building_id: data.building_id?.toString() || '',
        operations_manager_id: managerId?.toString() || ''
      });
    }
  }, [data, reset]);

  // Track page view
  useEffect(() => {
    mixpanel.track('Edit Block Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  // Function to restrict block area to only numbers and decimals
  const handleBlockAreaChange = useCallback(
    (val) => {
      // Allow numbers and a single decimal point
      const cleanedValue = val
        .replace(/[^0-9.]/g, '')
        .replace(/(\..*?)\..*/g, '$1')
        .slice(0, 8);
      setValue('area', cleanedValue);
    },
    [setValue]
  );

  return (
    <>
      <div>
        <div className="flex flex-col justify-between items-start gap-4">
          <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">Block details</span>
        </div>
        <div className="flex flex-col gap-4 pt-6">
          <div className="flex justify-between items-center w-full">
            <FormInput
              width={48}
              label={'Block Name'}
              register={register}
              register_key={'name'}
              type={'text'}
              defaultValuePre={data?.name}
            />

            <FormInput
              width={48}
              label={'Block area'}
              register={register}
              register_key={'area'}
              type={'text'}
              defaultValuePre={data?.area}
              onType={handleBlockAreaChange}
            />
          </div>
          <div className="flex gap-2 items-end justify-between w-full">
            <FormInput
              width={91}
              label={'Building Permission No'}
              register={register}
              register_key={'building_id'}
              type={'dropdown'}
              options={[
                { value: '', label: 'Select Building Permission No', disabled: true },
                ...buildingPermissionOptions
              ]}
            />
            <Button
              type="button"
              onClick={() => setIsOpenEditProject(true)}
              displayType="secondary"
              className="mb-1 px-3 py-2 text-sm h-10">
              Add
            </Button>
          </div>
          <div className="flex justify-between items-center w-full">
            <FormInput
              width={48}
              label={'Operations Manager (Optional)'}
              register={register}
              register_key={'operations_manager_id'}
              type={'dropdown'}
              options={[
                { value: '', label: 'Select Operations Manager', disabled: true },
                ...operationsManagerOptions
              ]}
            />
          </div>
        </div>

        <div className="w-full flex justify-end items-center space-x-4 mt-8">
          <Button type="reset" onClick={onClose} displayType="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit(handleProceed)} type="submit" disabled={isSubmitting}>
            Save
          </Button>
        </div>
      </div>
    </>
  );
};

export default EditBlock;
